import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { RichTextViewer } from '@/components/ui/rich-text-viewer';

interface LegalDocument {
  id: string;
  name: string;
  content: string;
  updated_at: string;
}

export default function DocumentsPage() {
  const { profile } = useProfile();
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewingDoc, setViewingDoc] = useState<LegalDocument | null>(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      setIsLoading(true);
      try {
        const roleScope = profile?.role === 'hoofdhartuser' ? 'hoofdhartuser' : null;
        let query = supabase
          .from('legal_documents')
          .select('*')
          .order('name');

        if (roleScope) {
          query = query.eq('role_scope', roleScope);
        } else {
          query = query.is('role_scope', null);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching documents:', error);
          return;
        }

        setDocuments(data ?? []);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Button variant="ghost" asChild>
            <Link to="/profiel">
              <ChevronLeft className="h-5 w-5 mr-1" />
              Terug naar profiel
            </Link>
          </Button>
        </div>

        <div className="bg-card rounded-lg p-6 border-2 border-green-500">
          <h3 className="text-lg font-medium mb-4">Documenten</h3>

          {isLoading ? (
            <div className="py-4 text-center text-muted-foreground">
              Laden van documenten...
            </div>
          ) : documents.length > 0 ? (
            <div className="space-y-3">
              {documents.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => setViewingDoc(doc)}
                  className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors text-left"
                >
                  <FileText className="h-5 w-5 text-blue-500 shrink-0" />
                  <div>
                    <p className="font-medium">{doc.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Laatst bijgewerkt:{' '}
                      {new Date(doc.updated_at).toLocaleDateString('nl-NL')}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="mb-4 text-muted-foreground">
              Er zijn momenteel geen documenten beschikbaar.
            </p>
          )}
        </div>
      </div>

      <Dialog
        open={viewingDoc !== null}
        onOpenChange={(open) => {
          if (!open) setViewingDoc(null);
        }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{viewingDoc?.name}</DialogTitle>
            <DialogDescription>
              Laatst bijgewerkt:{' '}
              {viewingDoc
                ? new Date(viewingDoc.updated_at).toLocaleDateString('nl-NL')
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 py-4">
            {viewingDoc?.content ? (
              <RichTextViewer content={viewingDoc.content} />
            ) : (
              <p className="text-muted-foreground text-center py-8">
                Dit document heeft nog geen inhoud.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
